import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import RenderMonitor from '../components/RenderMonitor';
import BootcampScheduleSection from '../components/BootcampScheduleSection';

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function daysBetween(a, b) {
  const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000));
}

function formatMonthYear(d) {
  return d.toLocaleString('en-AU', { month: 'short', year: 'numeric' });
}

function getMonthBounds(year, month) {
  const rangeStart = new Date(year, month, 1);
  const rangeEnd = new Date(year, month + 1, 0);
  return { rangeStart, rangeEnd, dayCount: rangeEnd.getDate() };
}

function toMonthInputValue(year, month) {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function parseMonthInputValue(value) {
  const [y, m] = String(value || '').split('-').map(Number);
  if (!y || !m) return null;
  return { year: y, month: m - 1 };
}

function AdminBootcampsCalendar() {
  const navigate = useNavigate();
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [bootcamps, setBootcamps] = useState([]);
  const [clients, setClients] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filterClient, setFilterClient] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  const { rangeStart, rangeEnd, dayCount } = useMemo(
    () => getMonthBounds(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  useEffect(() => {
    supabase.from('clients').select('id, business_name').then(({ data }) => {
      setClients(data || []);
    });
  }, []);

  useEffect(() => {
    if (!filterClient) {
      setRegions([]);
      setFilterRegion('');
      return;
    }
    supabase
      .from('regions')
      .select('id, name')
      .eq('client_id', filterClient)
      .eq('active', true)
      .then(({ data }) => setRegions(data || []));
  }, [filterClient]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const monthStartIso = startOfDay(rangeStart).toISOString();
        const monthEndIso = startOfDay(addDays(rangeEnd, 1)).toISOString();

        let teamQuery = supabase.from('teams').select('id, name, client_id, region_id, clients(business_name), regions(name)');
        if (filterClient) teamQuery = teamQuery.eq('client_id', filterClient);
        if (filterRegion) teamQuery = teamQuery.eq('region_id', filterRegion);

        const { data: teamsData, error: teamsErr } = await teamQuery;
        if (teamsErr) throw teamsErr;

        const teamIds = (teamsData || []).map((t) => t.id);
        if (teamIds.length === 0) {
          if (mounted) setBootcamps([]);
          setLoading(false);
          return;
        }

        const teamMeta = new Map((teamsData || []).map((t) => [String(t.id), t]));

        const { data: bootcampsData, error: bcErr } = await supabase
          .from('bootcamps')
          .select('id, start_date, end_date, team_id, courses(name)')
          .in('team_id', teamIds)
          .lt('start_date', monthEndIso)
          .gte('end_date', monthStartIso)
          .order('start_date', { ascending: true });

        if (bcErr) throw bcErr;

        const enriched = (bootcampsData || []).map((bc) => {
          const team = teamMeta.get(String(bc.team_id)) || {};
          const nowMs = Date.now();
          const startTs = new Date(bc.start_date).getTime();
          const endTs = new Date(bc.end_date).getTime();
          const status = startTs > nowMs ? 'Upcoming' : (endTs <= nowMs ? 'Completed' : 'Active');
          return {
            ...bc,
            clientId: String(team.client_id || ''),
            teamName: team.name || 'Team',
            clientName: team.clients?.business_name || 'Client',
            regionName: team.regions?.name || 'Region',
            courseName: bc.courses?.name || 'Bootcamp',
            status,
          };
        });

        if (mounted) setBootcamps(enriched);
      } catch {
        if (mounted) setError('Could not load bootcamps');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [filterClient, filterRegion, rangeStart, rangeEnd, calendarRefreshKey]);

  const monthSpans = useMemo(
    () => [{ label: formatMonthYear(rangeStart), span: dayCount }],
    [rangeStart, dayCount]
  );

  const dayHeaders = useMemo(() => {
    const headers = [];
    for (let i = 0; i < dayCount; i++) {
      const d = addDays(rangeStart, i);
      headers.push({ day: d.getDate(), key: d.toISOString() });
    }
    return headers;
  }, [rangeStart, dayCount]);

  const bootcampsByClient = useMemo(() => {
    const clientGroups = new Map();
    bootcamps.forEach((bc) => {
      const clientKey = bc.clientId || bc.clientName;
      if (!clientGroups.has(clientKey)) {
        clientGroups.set(clientKey, {
          clientId: clientKey,
          clientName: bc.clientName,
          courses: new Map(),
        });
      }
      const courseKey = bc.courseName || 'Bootcamp';
      const clientGroup = clientGroups.get(clientKey);
      if (!clientGroup.courses.has(courseKey)) {
        clientGroup.courses.set(courseKey, { courseName: courseKey, bootcamps: [] });
      }
      clientGroup.courses.get(courseKey).bootcamps.push(bc);
    });
    return Array.from(clientGroups.values())
      .sort((a, b) => String(a.clientName).localeCompare(String(b.clientName)))
      .map((group) => ({
        ...group,
        courses: Array.from(group.courses.values()).sort((a, b) =>
          String(a.courseName).localeCompare(String(b.courseName))
        ),
      }));
  }, [bootcamps]);

  const renderBootcampRow = (bc) => {
    const start = startOfDay(new Date(bc.start_date));
    const end = startOfDay(new Date(bc.end_date));
    const visibleStart = start < rangeStart ? rangeStart : start;
    const visibleEnd = end > rangeEnd ? rangeEnd : end;
    const offset = daysBetween(rangeStart, visibleStart);
    const span = daysBetween(visibleStart, visibleEnd) + 1;
    return (
      <div key={bc.id} className="bootcamp-calendar-row bootcamp-calendar-row-compact" style={{ gridTemplateColumns: gridCols }}>
        <div
          className="bootcamp-calendar-row-label bootcamp-calendar-row-label-compact"
          title={`${bc.regionName} / ${bc.teamName}`}
        >
          <span className="bootcamp-calendar-team">{bc.teamName}</span>
        </div>
        <div
          className="bootcamp-calendar-timeline bootcamp-calendar-timeline-compact"
          style={{ gridColumn: '2 / -1', gridTemplateColumns: `repeat(${dayCount}, minmax(28px, 1fr))` }}
        >
          <div
            className={`bootcamp-calendar-bar bootcamp-calendar-bar-compact bootcamp-calendar-bar-${bc.status.toLowerCase()}`}
            style={{ gridColumn: `${offset + 1} / span ${span}` }}
            onClick={() => navigate(`/bootcamp/${bc.id}`)}
            title={`${bc.courseName}: ${new Date(bc.start_date).toLocaleDateString()} – ${new Date(bc.end_date).toLocaleDateString()}`}
          >
            {bc.status}
          </div>
        </div>
      </div>
    );
  };

  const gridCols = `180px repeat(${dayCount}, minmax(28px, 1fr))`;

  const handleBootcampChanged = useCallback(() => {
    setCalendarRefreshKey((k) => k + 1);
  }, []);

  return (
    <div className="dashboard-page">
      <RenderMonitor componentName="AdminBootcampsCalendar" />
      <div className="modern-mb-6">
        <h1 className="modern-title">Bootcamps</h1>
      </div>

      <BootcampScheduleSection onBootcampChanged={handleBootcampChanged} />

      <div className="bootcamp-calendar-section modern-mb-6">
        <h2 className="modern-heading" style={{ marginBottom: 4 }}>Calendar</h2>
        <p className="modern-subtitle modern-mb-4">All bootcamps in the selected month</p>

      <div className="modern-card modern-mb-6">
        <div className="modern-flex modern-gap-4" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, color: '#64748b', marginBottom: 4 }}>Month</label>
            <input
              type="month"
              className="modern-input bootcamp-calendar-month-input"
              value={toMonthInputValue(viewYear, viewMonth)}
              onChange={(e) => {
                const parsed = parseMonthInputValue(e.target.value);
                if (!parsed) return;
                setViewYear(parsed.year);
                setViewMonth(parsed.month);
              }}
              style={{ minWidth: 160 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, color: '#64748b', marginBottom: 4 }}>Client</label>
            <select
              className="modern-input bootcamp-calendar-filter-input"
              value={filterClient}
              onChange={(e) => { setFilterClient(e.target.value); setFilterRegion(''); }}
              style={{ minWidth: 200 }}
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.business_name}</option>
              ))}
            </select>
          </div>
          {filterClient && (
          <div>
            <label style={{ display: 'block', fontSize: 13, color: '#64748b', marginBottom: 4 }}>Region</label>
            <select
              className="modern-input bootcamp-calendar-filter-input"
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              style={{ minWidth: 200 }}
            >
              <option value="">All regions</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="modern-loading">Loading calendar…</div>
      ) : error ? (
        <div className="modern-error">{error}</div>
      ) : bootcamps.length === 0 ? (
        <div className="modern-card" style={{ color: '#64748b' }}>
          No bootcamps in {formatMonthYear(rangeStart)} match your filters.
        </div>
      ) : (
        <div className="bootcamp-calendar-wrap modern-card">
          <div className="bootcamp-calendar-scroll">
            <div className="bootcamp-calendar-header" style={{ gridTemplateColumns: gridCols }}>
              <div className="bootcamp-calendar-corner" />
              {monthSpans.map((m) => (
                <div key={m.label} className="bootcamp-calendar-month" style={{ gridColumn: `span ${m.span}` }}>
                  {m.label}
                </div>
              ))}
            </div>
            <div className="bootcamp-calendar-header bootcamp-calendar-days-row" style={{ gridTemplateColumns: gridCols }}>
              <div className="bootcamp-calendar-corner" />
              {dayHeaders.map((h) => (
                <div key={h.key} className="bootcamp-calendar-day">{h.day}</div>
              ))}
            </div>
            {bootcampsByClient.map((group) => (
              <div key={group.clientId} className="bootcamp-calendar-client-group">
                <div
                  className="bootcamp-calendar-client-header"
                  style={{ gridTemplateColumns: gridCols }}
                >
                  <div className="bootcamp-calendar-client-name">{group.clientName}</div>
                  <div className="bootcamp-calendar-client-divider" style={{ gridColumn: '2 / -1' }} />
                </div>
                {group.courses.map((courseGroup) => (
                  <div key={courseGroup.courseName} className="bootcamp-calendar-course-group">
                    <div
                      className="bootcamp-calendar-course-header"
                      style={{ gridTemplateColumns: gridCols }}
                    >
                      <div className="bootcamp-calendar-course-name">{courseGroup.courseName}</div>
                      <div className="bootcamp-calendar-course-divider" style={{ gridColumn: '2 / -1' }} />
                    </div>
                    {courseGroup.bootcamps.map(renderBootcampRow)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default AdminBootcampsCalendar;
